import React, { useState, useEffect, useRef } from "react";
import { 
  Table, 
  Button, 
  Drawer, 
  Form, 
  Input, 
  Select, 
  message, 
  Space, 
  Tooltip,
  InputNumber,
  Tag,
  Spin,
  Card,
  Typography,
  Descriptions,
  List,
  Image,
  Divider,
  DatePicker,
  Modal
} from "antd";
import axios from "axios";
import { 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  PlusOutlined,
  DownloadOutlined,
  FileOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  PhoneOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  SnippetsOutlined,
  ToolOutlined
} from "@ant-design/icons";
import constants from "../../constant";
import { useAuth } from "../../context/authContext";
import TextArea from "antd/es/input/TextArea";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { Option } = Select;
const { Title, Text } = Typography;
function getQueryParam(url, param) {
    const params = new URL(url).searchParams;
    return params.get(param);
}
// Order Detail View Component
const OrderDetailView = ({ order, visible, onClose, onJobsCreated }) => {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderJobs, setOrderJobs] = useState([]);
  const [jobCreationLoading, setJobCreationLoading] = useState(false);
  const {currentUser, roles} = useAuth();
  const isAdmin = roles.includes('ADMIN');
  const isSales = roles.includes('SALES');
  
  useEffect(() => {
    if (order && visible) {
      setOrderDetails(order);
      fetchOrderJobs(order._id);
    }
  }, [order, visible]);

  const fetchOrderJobs = async (orderId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${constants.DOMAIN}/api/v1/jobs/order/${orderId}`);
      setOrderJobs(response.data.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch order jobs:', error);
      setOrderJobs([]);
    } finally {
      setLoading(false);
    }
  };
  
  const orderStatusColors = {
    PENDING_PROCESS: 'gold',
    PENDING_DESIGN: 'purple',
    PENDING_PRODUCTION: 'blue',
    PENDING_DISPATCH: 'cyan',
    INPROGRSS: 'orange',
    COMPLETED: 'green',
    LOST: 'red',
    CREATED: 'blue',
    DELIVERED: 'green',
    AMOUNT_PENDING: 'volcano'
  };
  
  const downloadAsset = (url) => {
    window.open(url, '_blank');
  };

  const handleCreateJobsFromDetail = async (orderId) => {
    Modal.confirm({
      title: 'Create Jobs',
      content: 'Are you sure you want to create jobs for this order? This will create individual jobs for each product in the order.',
      okText: 'Yes, Create Jobs',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setJobCreationLoading(true);
        try {
          const response = await axios.post(`${constants.DOMAIN}/api/v1/jobs/create-for-order/${orderId}`);
          message.success(`Successfully created ${response.data.data.count} jobs for this order!`);
          fetchOrderJobs(orderId); // Refresh the jobs list
          if (onJobsCreated) {
            onJobsCreated(); // Notify parent component to refresh
          }
        } catch (error) {
          console.error("Failed to create jobs:", error);
          if (error.response?.data?.message) {
            message.error(error.response.data.message);
          } else {
            message.error("Failed to create jobs for this order");
          }
        } finally {
          setJobCreationLoading(false);
        }
      }
    });
  };

  const getJobStatusColor = (status) => {
    const colors = {
      'PENDING_DESIGN': 'blue',
      'PENDING_ACCOUNTS': 'orange',
      'PENDING_PRODUCTION': 'purple', 
      'PENDING_DISPATCH': 'cyan',
      'DISPATCHED': 'green',
      'DELIVERED': 'success',
      'COMPLETED': 'green'
    };
    return colors[status] || 'default';
  };

  const getJobPriorityColor = (priority) => {
    const colors = { 'HIGH': 'red', 'MEDIUM': 'orange', 'LOW': 'green' };
    return colors[priority] || 'default';
  };

  const findJobForProduct = (productCode) => {
    return orderJobs.find(job => job.productCode === productCode);
  };
  
  if (!order) return null;

  // Helper function to safely get decimal values
  const getDecimalValue = (value) => {
    if (!value) return 0;
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };
  
  return (
    <Drawer
      title="Order Details"
      placement="right"
      onClose={() => onClose(false)}
      open={visible}
      width={700}
      footer={
        <div style={{ textAlign: 'right' }}>
          {orderJobs.length === 0 && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              loading={jobCreationLoading}
              onClick={() => handleCreateJobsFromDetail(orderDetails._id)}
              style={{ marginRight: 8 }}
            >
              Create Jobs
            </Button>
          )}
          <Button onClick={() => onClose(false)} style={{ marginRight: 8 }}>
            Close
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        {orderDetails ? (
          <>
            <Card className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <Title level={4}>Order Summary</Title>
                <div className="flex gap-2">
                  <Tag color={orderJobs.length > 0 ? 'green' : 'orange'}>
                    {orderJobs.length > 0 ? `${orderJobs.length} Jobs Created` : 'No Jobs Created'}
                  </Tag>
                  <Tag color={orderStatusColors[orderDetails.orderStatus] || 'default'}>
                    {orderDetails.orderStatus}
                  </Tag>
                </div>
              </div>
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Customer" span={2}>
                  {orderDetails.leadId?.name || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Contact">
                  {orderDetails.primaryPhone || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Lead Source">
                  {orderDetails.leadId?.source || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="GST Number">
                  {orderDetails.gstNumber || 'N/A'}
                </Descriptions.Item>
                {/* <Descriptions.Item label="GST Percentage">
                  {orderDetails.gstPercentage || '0'}%
                </Descriptions.Item> */}
                <Descriptions.Item label="Order Priority">
                  {orderDetails.orderPriority || 'MEDIUM'}
                </Descriptions.Item>
                <Descriptions.Item label="Dispatch Mode">
                  {orderDetails.dispatchMode || 'N/A'}
                </Descriptions.Item>
                {orderDetails.dispatchDate && (
                  <Descriptions.Item label="Dispatch Date">
                    {new Date(orderDetails.dispatchDate).toLocaleDateString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card className="mb-4">
              <Title level={4}>Address Information</Title>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Full Address">
                  {`${orderDetails.flat || ''} ${orderDetails.area || ''}`}<br />
                  {`${orderDetails.city || ''}, ${orderDetails.state || ''}`}<br />
                  {`Pincode: ${orderDetails.pincode || 'N/A'}`}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card className="mb-4">
              <Title level={4}>Products & Job Status</Title>
              <List
                itemLayout="horizontal"
                dataSource={orderDetails.products || []}
                renderItem={(item, index) => {
                  const job = findJobForProduct(item.productCode);
                  return (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <span style={{ fontWeight: 'bold' }}>
                              {`Product ${index + 1}: ${item.productName || item.productCode || 'Unknown Product'}`}
                            </span>
                            {item.size && <Tag color="blue">{item.size}</Tag>}
                            {job && (
                              <Tag color={getJobStatusColor(job.currentStatus)}>
                                Job: {job.currentStatus?.replace('_', ' ')}
                              </Tag>
                            )}
                            {job?.priority && (
                              <Tag color={getJobPriorityColor(job.priority)} size="small">
                                {job.priority}
                              </Tag>
                            )}
                            {job?.isUrgent && <Tag color="red" size="small">URGENT</Tag>}
                          </Space>
                        }
                        description={
                          <div>
                            {item.productCode && item.productName && (
                              <>
                                <Text type="secondary">Product Code: {item.productCode}</Text><br />
                              </>
                            )}
                            <Text>Quantity: {item.quantity}</Text><br />
                            {(isAdmin || isSales) && (
                              <>
                                <Text>Rate: ₹{item.rate?.toLocaleString('en-IN') || 0}</Text><br />
                                <Text>Total: ₹{((item.quantity || 0) * (item.rate || 0))?.toLocaleString('en-IN') || 0}</Text>
                                {item.gstPercentage && <><br /><Text>GST: {item.gstPercentage}%</Text></>}
                              </>
                            )}
                            {job && (
                              <>
                                <br />
                                <Text strong>Job Progress:</Text><br />
                                {job.design?.status === 'COMPLETED' && (
                                  <Text type="secondary">✅ Design: Completed</Text>
                                )}
                                {job.design?.status !== 'COMPLETED' && (
                                  <Text type="secondary">🎨 Design: {job.design?.status || 'Pending'}</Text>
                                )}
                                <br />
                                {job.production?.status === 'COMPLETED' && (
                                  <Text type="secondary">✅ Production: Completed</Text>
                                )}
                                {job.production?.status === 'IN_PROGRESS' && (
                                  <Text type="secondary">🏭 Production: In Progress</Text>
                                )}
                                {(!job.production?.status || job.production?.status === 'PENDING') && (
                                  <Text type="secondary">⏳ Production: Pending</Text>
                                )}
                                <br />
                                {job.dispatch?.status === 'DELIVERED' && (
                                  <Text type="secondary">✅ Dispatch: Delivered</Text>
                                )}
                                {job.dispatch?.status === 'DISPATCHED' && (
                                  <Text type="secondary">🚚 Dispatch: Dispatched</Text>
                                )}
                                {(!job.dispatch?.status || job.dispatch?.status === 'PENDING') && (
                                  <Text type="secondary">📦 Dispatch: Pending</Text>
                                )}
                                <br />
                                <Text type="secondary">
                                  💰 Accounts: {job.accounts?.status?.replace('_', ' ') || 'Unreconciled'}
                                </Text>
                                {job._id && (
                                  <>
                                    <br />
                                    <Button 
                                      size="small" 
                                      type="link" 
                                      onClick={() => window.open(`/jobs?orderId=${orderDetails._id}`, '_blank')}
                                    >
                                      View Job Details →
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                            {!job && (
                              <>
                                <br />
                                <Text type="secondary" style={{ color: '#ff4d4f' }}>
                                  ⚠️ No job found for this product
                                </Text>
                              </>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>

            {(isAdmin || isSales) && (
              <Card className="mb-4">
                <Title level={4}>Financial Details</Title>
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Amount (without GST)">
                    ₹{getDecimalValue(orderDetails.amountWithoutGST)?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount (with GST)">
                    ₹{getDecimalValue(orderDetails.amountWithGST)?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Additional Charges">
                    ₹{getDecimalValue(orderDetails.additionalCharges)?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Packaging Charges">
                    ₹{getDecimalValue(orderDetails.packagingCharges)?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Advance Amount">
                    ₹{getDecimalValue(orderDetails.advanceAmount)?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Balance">
                    ₹{(getDecimalValue(orderDetails.amountWithGST) - getDecimalValue(orderDetails.advanceAmount))?.toLocaleString('en-IN') || 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {orderDetails.comments && (
              <Card className="mb-4">
                <Title level={4}>Comments</Title>
                <Text>{orderDetails.comments}</Text>
              </Card>
            )}

            {orderDetails.Assets && orderDetails.Assets.length > 0 && (
              <Card className="mb-4">
                <Title level={4}>Assets</Title>
                <div className="grid grid-cols-2 gap-4">
                  {orderDetails.Assets.map((asset, index) => (
                    <Card key={index} size="small" className="mb-2">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <FileOutlined /> {getQueryParam(asset, "name") || `Asset ${index + 1}`}
                        </div>
                        <Button 
                          icon={<DownloadOutlined />} 
                          size="small"
                          onClick={() => downloadAsset(asset)}
                        >
                          Download
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center p-10">
            <Text>No order details available</Text>
          </div>
        )}
      </Spin>
    </Drawer>
  );
};

const OrderManagement_v1 = () => {
  const {currentUser, roles} = useAuth();
  const isAdmin = roles.includes('ADMIN')  ;
  const isSales = roles.includes('SALES')
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [jobCreationLoading, setJobCreationLoading] = useState({});
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      position: ["topCenter"],
    },
    filters: {},
  });
  const searchInput = useRef(null);
  const [form] = Form.useForm();

  // Fetch orders with filtering
  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = [];
      Object.keys(tableParams.filters).forEach((k) => {
        if (tableParams.filters[k]?.length > 0) {
          query.push(`${k}:sw=${tableParams.filters[k]}`);
        }
      });

      if(roles.includes("DESIGN")){
        query.push(`orderStatus:in=PENDING_DESIGN`);
      }
      if(roles.includes("PRODUCTION")){
        query.push(`orderStatus:in=PENDING_PRODUCTION`);
      }
      if(roles.includes("DELIVERY")){
        query.push(`orderStatus:in=PENDING_DISPATCH`);
      }
      query = `page=${tableParams.pagination.current}&limit=${tableParams.pagination.pageSize}&firebaseId=${currentUser._delegate.uid}&${query.join("&")}`;
      const res = await axios.get(`${constants.DOMAIN}/api/v1/marketing/getOrderData?${query}&version=2`);
      
      // Check if jobs exist for each order and fetch detailed job information
      const ordersWithJobStatus = await Promise.all(
        res.data.data.orders.map(async (order) => {
          try {
            const jobsResponse = await axios.get(`${constants.DOMAIN}/api/v1/jobs/order/${order._id}`);
            const jobs = jobsResponse.data.data.jobs || [];
            
            // Create a map of productCode to job for easy lookup
            const jobsByProduct = {};
            jobs.forEach(job => {
              if (job.productCode) {
                jobsByProduct[job.productCode] = job;
              }
            });
            
            return {
              ...order,
              hasJobs: jobs.length > 0,
              jobsByProduct: jobsByProduct,
              totalJobs: jobs.length
            };
          } catch (error) {
            return {
              ...order,
              hasJobs: false,
              jobsByProduct: {},
              totalJobs: 0
            };
          }
        })
      );
      
      setOrders(ordersWithJobStatus);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: res.data.results,
        },
      })
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      message.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Fetch system users for POC selection
  const fetchSystemUsers = async () => {
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/systemuser/getUser`);
      setSystemUsers(response.data.data.users);
    } catch (error) {
      message.error('Failed to fetch system users');
    }
  };

  const fetchProducts = async () => {
    // setIsLoadingProducts(true);
    try {
      let query = [];
      Object.keys(tableParams.filters).forEach((k) => {
        if (tableParams.filters[k]?.length > 0) {
          query.push(`${k}:sw=${tableParams.filters[k]}`);
        }
      });

      const res = await axios.get(`${constants.DOMAIN}/api/v1/products/?${query.join("&")}`);
      setProducts(res.data.data.products);
    } catch (error) {
      message.error('Failed to load products');
    } finally {
    //   setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchSystemUsers();
    fetchProducts()
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [tableParams.filters]);

  // Update form when currentOrder changes
  useEffect(() => {
    if (currentOrder) {
      form.setFieldsValue({
        primaryPhone: currentOrder.primaryPhone,
        orderStatus: currentOrder.orderStatus,
        pincode: currentOrder.pincode,
        flat: currentOrder.flat,
        area: currentOrder.area,
        city: currentOrder.city,
        state: currentOrder.state,
        poc: currentOrder.poc,
        advanceAmount: currentOrder.advanceAmount,
        dispatchMode: currentOrder.dispatchMode,
        gstNumber: currentOrder.gstNumber,
        gstPercentage: currentOrder.gstPercentage,
        packagingCharges: currentOrder.packagingCharges,
        additionalCharges: currentOrder.additionalCharges,
        amountWithoutGST: currentOrder.amountWithoutGST,
        amountWithGST: currentOrder.amountWithGST,
        comments: currentOrder.comments,
      });
    } else {
      form.resetFields();
    }
  }, [currentOrder, form]);

  const handleView = (order) => {
    setViewingOrder(order);
    setViewDrawerOpen(true);
  };

  const handleEdit = (order) => {
    setCurrentOrder(order);
    setIsDrawerOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Order',
      content: 'Are you sure you want to delete this order? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true);
        try {
          await axios.delete(`${constants.DOMAIN}/api/v1/marketing/deleteOrder/${id}`);
          message.success("Order deleted successfully");
          fetchOrders();
        } catch (error) {
          console.error("Failed to delete order:", error);
          message.error("Failed to delete order");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleCreateJobs = async (orderId) => {
    Modal.confirm({
      title: 'Create Jobs',
      content: 'Are you sure you want to create jobs for this order? This will create individual jobs for each product in the order.',
      okText: 'Yes, Create Jobs',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setJobCreationLoading(prev => ({ ...prev, [orderId]: true }));
        try {
          const response = await axios.post(`${constants.DOMAIN}/api/v1/jobs/create-for-order/${orderId}`);
          message.success(`Successfully created ${response.data.data.count} jobs for this order!`);
          fetchOrders(); // Refresh the table to show updated status
        } catch (error) {
          console.error("Failed to create jobs:", error);
          if (error.response?.data?.message) {
            message.error(error.response.data.message);
          } else {
            message.error("Failed to create jobs for this order");
          }
        } finally {
          setJobCreationLoading(prev => ({ ...prev, [orderId]: false }));
        }
      }
    });
  };

  const checkIfJobsExist = async (orderId) => {
    try {
      const response = await axios.get(`${constants.DOMAIN}/api/v1/jobs/order/${orderId}`);
      return response.data.data.jobs && response.data.data.jobs.length > 0;
    } catch (error) {
      return false;
    }
  };

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.post(`${constants.DOMAIN}/api/v1/marketing/updateOrder/new`, values);
      message.success("Order updated successfully");
      setIsDrawerOpen(false);
      fetchOrders();
      form.resetFields();
      setCurrentOrder(null);
    } catch (error) {
      console.error("Full error:", error);
      message.error("Failed to save order");
    } finally {
      setSubmitting(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={close}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setTableParams({
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
      filters: { [dataIndex]: selectedKeys },
    });
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    fetchOrders();
  };

  const orderStatusColors = {
    PENDING_PROCESS: 'gold',
    INPROGRSS: 'orange',
    COMPLETED: 'green',
    LOST: 'red',
    CREATED: 'blue'
  };

  const getDecimalValue = (value) => {
    if (!value) return 0;
    if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value) || 0;
  };

  const columns = [
    isAdmin ? {
      title: "POC",
      dataIndex: "poc",
      filters: (systemUsers || []).map((d) => ({
        value: d._id,
        text: d.name,
      })),
      render: (data, record) => {
        const pocName = systemUsers?.find(item => item._id === data)?.name || 'Not Assigned';
        return (
          <Select
            size="small"
            defaultValue={data}
            style={{ width: '100%', minWidth: 100 }}
            onChange={(e) => {
              record.poc = e;
            }}
            options={(systemUsers || []).map((d) => ({
              value: d._id,
              label: d.name,
            }))}
          >
          </Select>
        );
      },
      responsive: ['md'],
    } : { width: 0 },
    {
      title: "Customer",
      dataIndex: "leadId",
      render: (lead) => lead?.name || 'N/A',
    },
    {
      title: "Phone",
      dataIndex: "primaryPhone",
      ...getColumnSearchProps("primaryPhone")
    },
    {
      title: "Products",
      width: 320,
      dataIndex: "products",
      render: (products, record) => {
        if (!products || products.length === 0) return 'N/A';
        
        const getJobStatusColor = (status) => {
          const colors = {
            'PENDING_DESIGN': 'blue',
            'PENDING_ACCOUNTS': 'orange',
            'PENDING_PRODUCTION': 'purple', 
            'PENDING_DISPATCH': 'cyan',
            'DISPATCHED': 'green',
            'DELIVERED': 'success',
            'COMPLETED': 'green'
          };
          return colors[status] || 'default';
        };

        const getJobPriorityColor = (priority) => {
          const colors = { 'HIGH': 'red', 'MEDIUM': 'orange', 'LOW': 'green' };
          return colors[priority] || 'default';
        };
        
        return (
          <div style={{ maxWidth: 350 }}>
            {products.map((p, i) => {
              const job = record.jobsByProduct?.[p.productCode];
              return (
                <div key={i} style={{ marginBottom: i === products.length - 1 ? 0 : 8, border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                  <div style={{ fontWeight: 'bold' }}>
                    {p.productName || p.productCode || 'Unknown Product'}
                  </div>
                  {p.productCode && p.productName && (
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      Code: {p.productCode}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                    {p.size && <Tag size="small" color="blue">{p.size}</Tag>}
                    <span>x{p.quantity}</span>
                    {p.rate && <span style={{ color: '#666' }}>@ ₹{p.rate}</span>}
                  </div>
                  
                  {/* Job Status Section */}
                  <div style={{ marginTop: 4 }}>
                    {job ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                          <Tag size="small" color={getJobStatusColor(job.currentStatus)}>
                            {job.currentStatus?.replace('_', ' ') || 'Unknown'}
                          </Tag>
                          {job.priority && (
                            <Tag size="small" color={getJobPriorityColor(job.priority)}>
                              {job.priority}
                            </Tag>
                          )}
                          {job.isUrgent && <Tag size="small" color="red">URGENT</Tag>}
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#666' }}>
                          {job.design?.status === 'COMPLETED' && '✅ Design '}
                          {job.production?.status === 'COMPLETED' && '✅ Production '}
                          {job.dispatch?.status === 'DELIVERED' && '✅ Delivered'}
                          {job.dispatch?.status === 'DISPATCHED' && '🚚 Dispatched'}
                          {!job.dispatch?.status && job.production?.status !== 'COMPLETED' && '⏳ In Progress'}
                        </div>
                      </div>
                    ) : (
                      <Tag size="small" color="red">No Job</Tag>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
    },
    {
      title: "Status",
      width: 120,
      dataIndex: "orderStatus",
      render: (status) => (
        <Tag className="text-xs font-bold" color={orderStatusColors[status] || 'default'}>
          {status.replace(/_/g, " ")}
        </Tag>
      ),
      filters: [
        { text: 'Pending Process', value: 'PENDING_PROCESS' },
        { text: 'Pending Design', value: 'PENDING_DESIGN' },
        { text: 'Pending Production', value: 'PENDING_PRODUCTION' },
        { text: 'Pending Dispatch', value: 'PENDING_DISPATCH' },
        { text: 'In Progress', value: 'INPROGRSS' },
        { text: 'Completed', value: 'COMPLETED' },
        { text: 'Lost', value: 'LOST' },
        { text: 'Created', value: 'CREATED' },
        { text: 'Delivered', value: 'DELIVERED' },
        { text: 'Amount Pending', value: 'AMOUNT_PENDING' },
      ],
    },
    {
      title: "Priority",
      width: 100,
      dataIndex: "orderPriority",
      render: (priority) => {
        const priorityColors = {
          HIGH: 'red',
          MEDIUM: 'orange',
          LOW: 'green'
        };
        return priority ? (
          <Tag color={priorityColors[priority] || 'blue'}>
            {priority}
          </Tag>
        ) : 'N/A';
      },
      filters: [
        { text: 'High', value: 'HIGH' },
        { text: 'Medium', value: 'MEDIUM' },
        { text: 'Low', value: 'LOW' },
      ],
    },
    isAdmin || isSales ? {
      title: "Total Amount",
      width: 160,
      dataIndex: "products",
      render: (products) => {
        if (!products || products.length === 0) return 'N/A';
        
        const totalAmount = products.reduce((sum, product) => {
          const rate = product.rate || 0;
          const quantity = product.quantity || 0;
          return sum + (rate * quantity);
        }, 0);
        
        return totalAmount > 0 ? `₹${totalAmount.toLocaleString('en-IN')}` : 'N/A';
      },
      sorter: (a, b) => {
        const getTotalAmount = (products) => {
          if (!products || products.length === 0) return 0;
          return products.reduce((sum, product) => {
            const rate = product.rate || 0;
            const quantity = product.quantity || 0;
            return sum + (rate * quantity);
          }, 0);
        };
        return getTotalAmount(a.products) - getTotalAmount(b.products);
      },
    } : {width: 0},

    isAdmin || isSales ? {
      title: "Advance",
      dataIndex: "advanceAmount",
      render: (amount) => amount ? `₹${getDecimalValue(amount).toLocaleString('en-IN')}` : 'N/A',
    } : {width: 0},
    {
      title: "Jobs Status",
      width: 120,
      dataIndex: "hasJobs",
      render: (hasJobs, record) => {
        if (!hasJobs) {
          return <Tag color="orange">No Jobs</Tag>;
        }
        
        const jobStatuses = Object.values(record.jobsByProduct || {});
        const totalJobs = jobStatuses.length;
        
        if (totalJobs === 0) {
          return <Tag color="orange">No Jobs</Tag>;
        }
        
        // Count jobs by status
        const statusCounts = {};
        jobStatuses.forEach(job => {
          const status = job.currentStatus || 'UNKNOWN';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        
        // Show summary
        const statusEntries = Object.entries(statusCounts);
        if (statusEntries.length === 1) {
          const [status, count] = statusEntries[0];
          const color = status === 'COMPLETED' ? 'green' : 
                       status === 'DELIVERED' ? 'success' :
                       status === 'DISPATCHED' ? 'cyan' :
                       status === 'PENDING_DISPATCH' ? 'blue' :
                       status === 'PENDING_PRODUCTION' ? 'purple' :
                       status === 'PENDING_DESIGN' ? 'blue' : 'default';
          return (
            <div>
              <Tag color={color}>
                {count}/{totalJobs} {status.replace('_', ' ')}
              </Tag>
            </div>
          );
        }
        
        // Multiple statuses - show count
        return (
          <div>
            <Tag color="blue">{totalJobs} Jobs</Tag>
            <div style={{ fontSize: '0.75em', marginTop: 2 }}>
              {statusEntries.map(([status, count]) => (
                <div key={status} style={{ color: '#666' }}>
                  {count} {status.replace('_', ' ')}
                </div>
              ))}
            </div>
          </div>
        );
      },
      filters: [
        { text: 'Jobs Created', value: true },
        { text: 'Jobs Pending', value: false },
      ],
      onFilter: (value, record) => record.hasJobs === value,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <Space size="small" className="flex flex-wrap gap-1">
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          {(isAdmin || isSales) && <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>}
          {(isAdmin || isSales) && <Tooltip title="Summary">
            <Button size="small" icon={<SnippetsOutlined />} onClick={() => {
              localStorage.setItem("invoice",JSON.stringify(record))
              navigate(`/receipt`)
            }} />
          </Tooltip>}
          <Tooltip title="View Jobs">
            <Button size="small" icon={<ToolOutlined />} onClick={() => {
              navigate(`/jobs?orderId=${record._id}`)
            }} />
          </Tooltip>
          {!record.hasJobs && (
            <Tooltip title="Create Jobs">
              <Button 
                size="small" 
                type="primary" 
                ghost
                icon={<PlusOutlined />}
                loading={jobCreationLoading[record._id]}
                onClick={() => handleCreateJobs(record._id)}
              >
                Jobs
              </Button>
            </Tooltip>
          )}
          {isAdmin && (
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ].filter(col => col.width !== 0);


  const OrderDrawer = ({ 
    isDrawerOpen, 
    setIsDrawerOpen, 
    currentLead,
    systemUsers, 
    products,
    handleOrderSubmit
  }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [files, setFiles] = useState([]);
    const [error, setError] = useState("");
     console.log(currentLead);
     
    // Initialize form when drawer opens or currentLead changes
    useEffect(() => {
      if (isDrawerOpen) {
        form.resetFields();
        setFiles([]);
        setError("");
        const getFormDecimalValue = (value) => {
          if (!value) return null;
          if (value.$numberDecimal) return parseFloat(value.$numberDecimal);
          return parseFloat(value) || null;
        };
        // If we have a currentLead, prepopulate some fields
        if (currentLead) {
          form.setFieldsValue({
            name: currentLead?.leadId?.name,
            primaryPhone: currentLead?.primaryPhone,
            city: currentLead?.city,
            poc: currentLead?.poc,
            products: currentLead?.products,
            additionalCharges: getFormDecimalValue(currentLead.additionalCharges),
            packagingCharges: getFormDecimalValue(currentLead.packagingCharges),
            amountWithoutGST: getFormDecimalValue(currentLead.amountWithoutGST),
            gstpercentage: currentLead.gstPercentage,
            amountWithGST: getFormDecimalValue(currentLead.amountWithGST),
            dispatch: currentLead.dispatchMode,
            advanceAmount: getFormDecimalValue(currentLead.advanceAmount),
            gstNumber: currentLead.gstNumber,
            pincode: currentLead.pincode,
            flat: currentLead.flat,
            area: currentLead.area,
            state: currentLead.state,
            comments: currentLead.comments,
            orderStatus: currentLead.orderStatus,
            orderPriority: currentLead.orderPriority || 'MEDIUM',
            dispatchDate: currentLead.dispatchDate ? dayjs(currentLead.dispatchDate) : null
          });
          setFiles(currentLead.Assets)
          calculateTotals()
        }
      }
    }, [isDrawerOpen, currentLead, form]);
  
    // Handle file change
    const handleFileChange = (event) => {
      const selectedFiles = Array.from(event.target.files);
      setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };
  
    // Remove file
    const removeFile = async(index) => {
      const removedFile = files[index]
      try {
        // setStatus({ loading: true, error: false, success: false })
        const url = `${constants.DOMAIN}/api/v1/marketing/cloudinary/delete`
         await axios.post(url, {
          publicId: getQueryParam(removedFile,"publicId")
        })
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
      } catch (error) {
        console.log(error)
      }
      
    };
  
    // Upload files
    const handleUpload = async (e) => {
        const uploadedUrls = [];
        const maxSize = 1024 * 1024 * 10; // 10MB limit
        const files = e.target.files;
        if (!files.length) return;
      
        console.log(files);
      
        // Validate file sizes
        for (let file of files) {
          if (file.size > maxSize) {
            setError(`File "${file.name}" is larger than 10MB`);
            return;
          }
        }
      
        try {
          const uploadPromises = Array.from(files).map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", "wdqav8pw");
            formData.append("public_id", `${currentLead?._id}-${Date.now()}`);
            formData.append("folder", "assets");
      
            const res = await axios.post(
              "https://api.cloudinary.com/v1_1/drb2pi1dv/raw/upload/",
              formData
            );
      
            return res.data.url + "?publicId=" + res.data.public_id + "&name=" + file.name;
          });
      
          // Wait for all uploads to finish
          const results = await Promise.all(uploadPromises);
          uploadedUrls.push(...results);
      
          setFiles((prevFiles) => [...prevFiles, ...uploadedUrls]);
        } catch (err) {
          console.error("Upload error:", err);
          setError("Something Went Wrong: " + (err.response?.data?.error?.message || err.message));
        }
      };
      
    console.log(files);
    
    // Calculate totals
    const calculateTotals = () => {
      try {
        const products = form.getFieldValue('products') || [];
        const additionalCharges = form.getFieldValue('additionalCharges') || 0;
        const packagingCharges = form.getFieldValue('packagingCharges') || 0;
        
        // Calculate products subtotal with individual GST
        let productsTotal = 0;
        let totalGSTAmount = 0;
        
        products.forEach(product => {
          if (product && product.quantity && product.rate) {
            const productSubtotal = product.quantity * product.rate;
            // Calculate individual product GST based on its own percentage
            const productGSTPercentage = product.gstPercentage || 0;
            const productGST = productSubtotal * (parseFloat(productGSTPercentage) / 100);
            totalGSTAmount += productGST;
            productsTotal += productSubtotal;
          }
        });
        
        // Calculate total without GST
        const totalWithoutGST = productsTotal + additionalCharges + packagingCharges;
        form.setFieldsValue({ totalWithoutGST });
        
        // Calculate GST on additional and packaging charges
        const defaultGSTPercentage = form.getFieldValue('defaultGSTPercentage') || 0;
        const additionalGST = additionalCharges * (parseFloat(defaultGSTPercentage) / 100);
        const packagingGST = packagingCharges * (parseFloat(defaultGSTPercentage) / 100);
        totalGSTAmount += additionalGST + packagingGST;
        
        // Calculate total with GST
        const totalWithGST = totalWithoutGST + totalGSTAmount;
        form.setFieldsValue({ totalWithGST, totalGSTAmount });
      } catch (error) {
        console.error("Error calculating totals:", error);
      }
    };
  
    // Handle form submission
    const handleFormSubmit = async (values) => {
      try {
        setSubmitting(true);
        setError("");
        
               
        // Add file URLs to form values
        const updatedValues = {
          ...values,
          fileUrls:files,
          orderId: currentLead?._id,
          leadId:currentLead?.leadId?._id
        };
        
        // Submit the form with updated values
        await handleOrderSubmit(updatedValues);
        
        // Close the drawer and reset
        message.success("Order created successfully!");
        setIsDrawerOpen(false);
        form.resetFields();
        setFiles([]);
      } catch (error) {
        message.error(`Failed to create order: ${error.message}`);
      } finally {
        setSubmitting(false);
      }
    };

    const statusOptions = [
        "PENDING_PROCESS",
        "PENDING_DESIGN",
        "PENDING_PRODUCTION",
        "PENDING_DISPATCH",
        "DELIVERED",
        "AMOUNT_PENDING",
        "COMPLETED",
      ];
  
    return (
      <Drawer
        title={
          <Space>
            <ShoppingCartOutlined />
            {currentLead ? "Edit Order" : "Create Order"}
          </Space>
        }
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          form.resetFields();
          setFiles([]);
          setError("");
        }}
        width={800}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsDrawerOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={() => form.submit()} 
              loading={submitting}
              icon={<ShoppingCartOutlined />}
            >
              Create Order
            </Button>
          </Space>
        }
      >
        <Spin spinning={submitting}>
          <Form
            form={form}
            onFinish={handleFormSubmit}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              // Recalculate totals whenever form values change
              calculateTotals();
            }}
          >
            {/* Customer Information */}
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter name' }]}
              >
                <Input
                  className="w-full"
                  placeholder="Enter Name"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
              <Form.Item
                name="primaryPhone"
                label="Primary Phone"
                rules={[
                  { required: true, message: 'Please enter phone number' },
                  { pattern: /^[0-9]{10}$/, message: 'Please enter valid phone number' }
                ]}
              >
                <Input 
                  placeholder="Enter phone number" 
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input
                  className="w-full"
                  placeholder="Enter City"
                />
              </Form.Item>
              <Form.Item
                name="poc"
                label="Point of Contact"
                rules={[{ required: true, message: 'Please select POC' }]}
              >
                <Select placeholder="Select POC">
                  {systemUsers?.map(user => (
                    <Option key={user._id} value={user._id}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item 
      label="Status" 
      name="orderStatus"
      rules={[{ required: true, message: "Please select a status!" }]}
    >
      <Select placeholder="Select Status" allowClear>
        {statusOptions.map((status) => (
          <Option key={status} value={status}>
            {status.replace(/_/g, " ")} {/* Converts "PENDING_PROCESS" → "PENDING PROCESS" */}
          </Option>
        ))}
      </Select>
    </Form.Item>
            </div>
  
            {/* File Upload Section */}
         
  
            {/* Products Section */}
            <div className='bg-[ghostwhite] p-6 pb-[10px]'>
            <Form.List name="products">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, i) => (
                    <div key={i} className="mb-6 border-b pb-4">
                      <h1 style={{ textAlign: "center", fontSize: "14px" }}>
                        Product {i + 1}
                        <span>
                          <MinusCircleOutlined
                            style={{ color: "red", marginLeft: "20px" }}
                            onClick={() => {
                              remove(name);
                              // Recalculate after removing a product
                              setTimeout(calculateTotals, 0);
                            }}
                          />
                        </span>
                      </h1>
                      <div className='flex gap-3 justify-center items-center mt-2'>
                        <Form.Item
                          {...restField}
                          name={[name, "productCode"]}
                          label="Product Code"
                          rules={[{ required: true, message: 'Please select product' }]}
                        >
                          <Select placeholder="Select product" allowClear>
                            {products.map(product => (
                              <Option key={product.code} value={product.code}>
                                {product.name} ({product.code})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "productName"]}
                          label="Product Name"
                          rules={[{ required: true, message: 'Please enter product name' }]}
                        >
                          <Input
                            className="w-full"
                            placeholder="Enter product name"
                          />
                        </Form.Item>
                      </div>
                      <div className='flex gap-3 justify-center items-center mt-2'>
                        <Form.Item
                          {...restField}
                          name={[name, "quantity"]}
                          label="Quantity"
                          rules={[{ required: true, message: 'Please enter quantity' }]}
                        >
                          <InputNumber
                            className="w-full"
                            placeholder="Enter quantity"
                            min={1}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "rate"]}
                          label="Rate"
                          rules={[{ required: true, message: 'Please enter Rate' }]}
                        >
                          <InputNumber
                            className="w-full"
                            placeholder="Enter amount"
                            min={0}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "size"]}
                          label="Size"
                          rules={[{ required: true, message: 'Please enter size' }]}
                        >
                          <Input
                            className="w-full"
                            placeholder="Enter size (e.g., 10x12, M, 36EU)"
                          />
                        </Form.Item>
                      </div>
                      <div className='flex gap-3 justify-center items-center mt-2'>
                        <Form.Item
                          {...restField}
                          name={[name, "gstPercentage"]}
                          label="GST %"
                          rules={[{ required: true, message: 'Please select GST percentage' }]}
                        >
                          <Select placeholder="Select GST">
                            <Option value="18">18 %</Option>
                            <Option value="12">12 %</Option>
                            <Option value="5">5 %</Option>
                            <Option value="0">0 %</Option>
                          </Select>
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                  <Form.Item
                    wrapperCol={{
                      span: 20,
                    }}
                    style={{ backgroundColor: "ghostwhite", display: "flex", justifyContent: "center" }}
                  >
                    <Button
                      className='w-max'
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Product
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
  
            {/* Financial Details Section */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mt-4">
              <h3 className="text-lg font-semibold mb-4 text-blue-800">💰 Financial Details</h3>
              <div className="grid grid-cols-3 gap-4">
                <Form.Item
                  name="additionalCharges"
                  label="Additional Charges"
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Enter amount"
                    min={0}
                  />
                </Form.Item>
                <Form.Item
                  name="packagingCharges"
                  label="Packaging Charges"
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Enter amount"
                    min={0}
                  />
                </Form.Item>
                <Form.Item
                  name="defaultGSTPercentage"
                  label="GST % (for Additional/Packaging)"
                  rules={[{ required: true, message: 'Please select GST percentage' }]}
                >
                  <Select placeholder="Select GST %">
                    <Option value="18">18%</Option>
                    <Option value="12">12%</Option>
                    <Option value="5">5%</Option>
                    <Option value="0">0%</Option>
                  </Select>
                </Form.Item>
              </div>
              
              {/* Amount Summary */}
              <div className="grid grid-cols-2 gap-4 mt-4 bg-white p-4 rounded border">
                <Form.Item
                  name="totalWithoutGST"
                  label="Amount (Excluding GST)"
                  rules={[{ required: true, message: 'Please enter amount' }]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Auto-calculated"
                    min={0}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
                <Form.Item
                  name="totalGSTAmount"
                  label="Total GST Amount"
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Auto-calculated"
                    min={0}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
                <Form.Item
                  name="totalWithGST"
                  label="Amount (Including GST)"
                  rules={[{ required: true, message: 'Please enter amount' }]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Auto-calculated"
                    min={0}
                    disabled
                    style={{ backgroundColor: '#f0f9ff', fontWeight: 'bold' }}
                  />
                </Form.Item>
                <Form.Item
                  name="advanceAmount"
                  label="Advance Amount"
                  rules={[{ required: true, message: 'Please enter amount' }]}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Enter advance amount"
                    min={0}
                  />
                </Form.Item>
              </div>
            </div>

            {/* Dispatch & Order Details */}
            <div className="bg-green-50 p-6 rounded-lg border border-green-200 mt-4">
              <h3 className="text-lg font-semibold mb-4 text-green-800">🚚 Dispatch & Order Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="dispatch"
                  label="Dispatch Mode"
                  rules={[{ required: true, message: 'Please select dispatch mode' }]}
                >
                  <Select placeholder="Select Mode">
                    <Option value="BUS">Bus</Option>
                    <Option value="TRAIN">Train</Option>
                    <Option value="FLIGHT">Flight</Option>
                    <Option value="COURIER">Courier</Option>
                    <Option value="SELF">Self</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="orderThrough"
                  label="Order Through" 
                  rules={[{ required: true, message: 'Please enter order through' }]}
                >
                  <Input
                    className="w-full"
                    placeholder="Enter order source (e.g., Phone, WhatsApp, Email, etc.)"
                  />
                </Form.Item>
                <Form.Item
                  name="gstNumber"
                  label="GST Number"
                >
                  <Input
                    className="w-full"
                    placeholder="Enter GST Number"
                  />
                </Form.Item>
              </div>
            </div>
  
            {/* Address Information */}
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="pincode"
                label="Pincode"
              >
                <InputNumber
                  className="w-full"
                  placeholder="Enter pincode"
                  min={100000}
                  max={999999}
                />
              </Form.Item>
  
              <Form.Item
                name="flat"
                label="Flat/House No."
              >
                <Input placeholder="Enter flat/house no." />
              </Form.Item>
  
              <Form.Item
                name="area"
                label="Area/Street"
              >
                <Input placeholder="Enter area/street" />
              </Form.Item>
  
              <Form.Item
                name="state"
                label="State"
              >
                <Input placeholder="Enter state" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4 mb-4 bg-[ghostwhite] p-6 rounded-lg border border-gray-300">
            <Form.Item
              name="orderPriority"
              label="Order Priority"
              rules={[{ required: true, message: 'Please select priority' }]}
            >
              <Select placeholder="Select Priority">
                <Option value="HIGH">High</Option>
                <Option value="MEDIUM">Medium</Option>
                <Option value="LOW">Low</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="dispatchDate"
              label="Expected Dispatch Date"
              rules={[{ required: true, message: 'Please select dispatch date' }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
  
            {/* Comments Section */}
            <div className="mt-4 mb-4 bg-[ghostwhite] p-6 rounded-lg border border-gray-300">
            <div className="mt-4 mb-4 bg-[ghostwhite] p-6 rounded-lg border border-gray-300">
              <h3 className="mb-3 text-lg font-semibold">Upload Files</h3>
              <label className="block w-full cursor-pointer border-dashed border-2 border-gray-400 p-4 rounded-lg text-center bg-white hover:bg-gray-100 transition">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleUpload}
                />
                <UploadOutlined className="text-2xl text-gray-600 mb-2" />
                <p className="text-gray-700">Click to select files</p>
                <p className="text-sm text-gray-500">Supports multiple file uploads</p>
              </label>
  
              {files.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between bg-white p-2 rounded-lg shadow-sm border border-gray-200"
                    >
                      <span className="text-gray-800 text-sm truncate">{getQueryParam(file,"name")}</span>
                      <button onClick={() => removeFile(index)} type="button">
                        <DeleteOutlined className="text-red-500 text-lg hover:text-red-700 transition" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              
              {error && <p className="text-red-500 mt-2">{error}</p>}
            </div>
              <Form.Item name="comments" label="Comments">
                <TextArea 
                  rows={4} 
                  placeholder="Add a comment..."
                />
              </Form.Item>
            </div>
          </Form>
        </Spin>
      </Drawer>
    );
  };

  const containerClasses = "p-2 md:p-6 bg-gray-50 min-h-screen";
  const cardClasses = "bg-white shadow-md rounded-lg p-2 md:p-6";
  const headerClasses = "flex flex-col md:flex-row justify-between items-center mb-4 md:mb-6 gap-2";
  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    })

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setOrders([])
    }
  }
  return (
    <div className="p-6">
     <OrderDetailView 
       order={viewingOrder} 
       visible={viewDrawerOpen} 
       onClose={setViewDrawerOpen} 
       onJobsCreated={fetchOrders}
     />
    <OrderDrawer 
     isDrawerOpen ={isDrawerOpen}
     setIsDrawerOpen ={setIsDrawerOpen} 
     currentLead={currentOrder}
     systemUsers={systemUsers} 
     products={products}
     handleOrderSubmit={handleFormSubmit}
    />
    <div className={cardClasses}>
        <div className={headerClasses}>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">
            Order Management
          </h1>
          <div className='flex gap-2'>
          <Button 
            type='dashed'
            onClick={() => {
             setTableParams((prev)=>{
              return {...prev,filters: {}}
             })
            }}
          >
            Clear Filters
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              setCurrentOrder(null);
              setIsDrawerOpen(true);
             
            }}
          >
            Add New Order
          </Button>
          </div>
        </div>
       
      <Spin spinning={loading}>
        <Table
          dataSource={orders}
          rowKey="_id"
          columns={columns}
          pagination={{
            ...tableParams.pagination,
            responsive: true,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="small"
          className="w-full"
        />
      </Spin>
      </div>
      
{/* 
      <Drawer
        title={currentOrder ? "Edit Order" : "Add Order"}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setCurrentOrder(null);
          form.resetFields();
        }}
        width={600}
      >
        <Spin spinning={submitting}>
          <Form
            form={form}
            onFinish={handleFormSubmit}
            layout="vertical"
          >
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Please input the amount!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="rate"
                label="Rate"
                rules={[{ required: true, message: "Please input the rate!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="otherCharges"
                label="Other Charges"
                rules={[{ required: true, message: "Please input other charges!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="orderValueIncludingGST"
                label="Final Amount (GST)"
                rules={[{ required: true, message: "Please input the final amount!" }]}
              >
                <InputNumber
                  className="w-full"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>

              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: "Please input the quantity!" }]}
              >
                <InputNumber className="w-full" min={1} />
              </Form.Item>

              <Form.Item
                name="productCode"
                label="Product Code"
                rules={[{ required: true, message: "Please input the product code!" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="primaryPhone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please input the phone number!" },
                  { pattern: /^\+?[\d\s-]{10,}$/, message: "Please enter a valid phone number!" }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="orderStatus"
                label="Status"
                rules={[{ required: true, message: "Please select the status!" }]}
              >
                <Select>
                  <Option value="CREATED">Created</Option>
                  <Option value="INPROGRSS">In Progress</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="LOST">Lost</Option>
                </Select>
              </Form.Item>

              {isAdmin && (
                <Form.Item
                  name="poc"
                  label="Point of Contact"
                  rules={[{ required: true, message: "Please select a POC!" }]}
                >
                  <Select>
                    {systemUsers.map(user => (
                      <Option key={user._id} value={user._id}>
                        {user.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="pincode"
                label="Pincode"
              >
                <InputNumber className="w-full" />
              </Form.Item>

              <Form.Item
                name="flat"
                label="Flat/Building"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="area"
                label="Area"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="city"
                label="City"
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="state"
                label="State"
              >
                <Input />
              </Form.Item>
            </div>

            <Form.Item className="mt-4">
              <Button type="primary" htmlType="submit" loading={submitting} block>
                {currentOrder ? "Update Order" : "Create Order"}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Drawer> */}
      </div>
  );
};

export default OrderManagement_v1;